import {
    BADGES,
    BADGE_VARIANT_ORDER,
    BUCKETS,
    INTEGRATION_BADGE_LABELS,
    SHIPPER_BADGES,
    SHIPPER_BUCKETS,
} from './constants';

const INTEGRATION_STATUS_NAMES = Object.values(INTEGRATION_BADGE_LABELS).map((label) =>
    label.replace('Integration ', '')
);

// What puts a carrier in each tab, as short bullets for the corporate admin (carriers, not the view's columns).
const TAB_GUIDE = {
    ACCOUNT_SETUP: [
        "Every Fulfillment Partner and Motor Carrier compliance hasn't approved, and approved ones with setup unfinished",
        "Uploaded: added, not invited, hasn't signed up",
        "Invited: invited, hasn't signed up",
        'In Progress: signed up (approved ones with setup unfinished land here), or rejected by compliance',
        'Awaiting Compliance: company info and documents done; Fulfillment Partners also need integrations started and coverage resources',
    ],
    COMPLIANCE: [
        'Every Fulfillment Partner and Motor Carrier',
        'Awaiting Review, Approved or Denied, from the decisions on its documents',
    ],
    INTEGRATIONS: [
        'Every Fulfillment Partner, and Motor Carriers that need pricing',
        'Awaiting Information: needs pricing, or no integration started',
        "In Progress: an integration that isn't live yet",
        'Integration Live: pricing set and the integration live (or not required)',
    ],
    LIVE: [
        'Account Setup done, approved by compliance, and Integrations done',
        'Ready To Link: a Fulfillment Partner on none of your Auto Assign profiles',
        'Live: linked',
    ],
};

/** Shown above the tab list: the one rule that applies across tabs. */
const TABS_INTRO =
    'A carrier can be in more than one tab at once: Compliance lists everyone, and Integrations every Fulfillment Partner. Within a tab it shows once, in the furthest section it has reached.';

// What each badge means, as short bullets. `sample` overrides the example badge's label where the real one varies.
const BADGE_GUIDE = {
    INVITED: {
        points: ['Motor Carrier waiting on compliance', "Fulfillment Partner that hasn't signed up"],
    },
    ACCOUNT_INFO_NEEDED: {
        points: [
            "Company Info's General Information isn't filled in",
            'Needs Name, DOT # or MC #, EIN #, and the billing address, city, state, zip and email',
        ],
    },
    REQUESTED_DOCUMENTS: {
        points: ['Missing an Agreement, Insurance or W9', 'Or one of them has expired'],
    },
    IN_COMPLIANCE: {
        points: ['Complete documents', 'Waiting on compliance to review'],
    },
    APPROVED: {
        points: ['Approved by compliance'],
    },
    REJECTED: {
        points: ['Rejected by compliance'],
    },
    NEEDS_PRICING: {
        points: [
            'Fulfillment Partner with no pricing with your shippers',
            'Motor Carrier with no rate on any of your Flex Centers',
        ],
    },
    INTEGRATION: {
        sample: INTEGRATION_BADGE_LABELS.NOT_STARTED,
        points: [
            "Fulfillment Partner whose integration isn't live and is required",
            `Shows the status: ${INTEGRATION_STATUS_NAMES.join(', ')}`,
        ],
    },
    NO_INTEGRATIONS: {
        points: ['Fulfillment Partner marked No Integrations Required', 'Skips the Integrations tab'],
    },
    LIVE: {
        points: ['Finished onboarding'],
    },
};

const SHIPPER_TAB_GUIDE = {
    ACCOUNT_SETUP: [
        'No pricing of its own, or a connected carrier with no pay for it',
        'No connected carriers',
        'Auto routing with no delivery profile',
    ],
    INTEGRATIONS: [
        'No integration set up, or one that is failing or needs info',
        'Mark No Integrations Required skips this step',
    ],
    LIVE: ['Pricing, carriers and integrations all set up'],
};

const SHIPPER_BADGE_GUIDE = {
    NO_INTEGRATIONS_SETUP: { points: ['No integration of its own or from your org defaults'] },
    INTEGRATION_FAILING: { points: ['An integration is failing'] },
    INTEGRATION_NEEDS_INFO: { points: ["An integration is waiting on the shipper's details"] },
    NO_PRICING: { points: ['No rates set up for the shipper'] },
    NO_CONNECTED_CARRIERS: { points: ['No carrier linked by an integration, pricing or partnership'] },
    CARRIERS_MISSING_PRICING: { points: ['A connected carrier has no pay set up for this shipper'] },
    NO_DELIVERY_PROFILE: { points: ['Auto routing, but no delivery profile assigned'] },
    NO_INTEGRATIONS: { points: ['Marked No Integrations Required', 'Skips the Integrations tab'] },
    NO_ORDERS: { points: ["Live, but hasn't sent an order yet"] },
    LIVE: { points: ['Finished onboarding'] },
};

/** Each kind's guide: dialog title, intro above the tabs, and the tabs and badges it explains. */
const GUIDES = {
    carrier: {
        title: 'Carrier onboarding',
        intro: TABS_INTRO,
        buckets: BUCKETS,
        badges: BADGES,
        tabs: TAB_GUIDE,
        badgeGuide: BADGE_GUIDE,
    },
    shipper: {
        title: 'Shipper onboarding',
        intro: 'A shipper can be in Account Setup and Integrations at once; Live means neither.',
        buckets: SHIPPER_BUCKETS,
        badges: SHIPPER_BADGES,
        tabs: SHIPPER_TAB_GUIDE,
        badgeGuide: SHIPPER_BADGE_GUIDE,
    },
};

/** The guide dialog's title and intro for `kind`. */
export const guideHeading = (kind = 'carrier') => ({ title: GUIDES[kind].title, intro: GUIDES[kind].intro });

/** The tabs in page order, with their guide bullets. */
export const tabGuideEntries = (kind = 'carrier') => {
    const { buckets, tabs } = GUIDES[kind];
    return buckets.map((bucket) => ({ ...bucket, points: tabs[bucket.key] || [] }));
};

const colourRank = (badge) => BADGE_VARIANT_ORDER.indexOf(badge.variant);

/**
 * The badges grouped by colour (green, blue, purple, yellow, red; card order within a colour), with their guide
 * bullets and the label the example badge shows.
 */
export const badgeGuideEntries = (kind = 'carrier') => {
    const { badges, badgeGuide } = GUIDES[kind];
    const byColour = [...badges].sort((a, b) => colourRank(a) - colourRank(b));
    return byColour.map((badge) => ({
        ...badge,
        sample: badgeGuide[badge.key]?.sample || badge.label,
        points: badgeGuide[badge.key]?.points || [],
    }));
};
